import React, { useCallback, useEffect, useState } from "react";
import { getGoogleApiKeys, saveGoogleApiKey } from "../../api/apiServiceFetch";
import "./GoogleApiKeys.scss";

// Совпадает с GoogleApiKeyPlatforms на бэкенде.
const PLATFORMS = {
	web: {
		label: "Веб",
		purpose: "Карта на сайте",
		restriction: "Ограничить доменами Jerm (HTTP referrers)",
	},
	server: {
		label: "Сервер",
		purpose: "Поиск мест с сервера",
		restriction: "Ограничить IP сервера",
	},
	android: {
		label: "Android",
		purpose: "Карта в приложении",
		restriction: "Ограничить приложением Android (package + SHA-1)",
	},
	ios: {
		label: "iPhone",
		purpose: "Карта в приложении",
		restriction: "Ограничить приложением iOS (bundle id)",
	},
};

const platformLabel = (platform) => PLATFORMS[platform]?.label ?? platform;

const formatUpdatedAt = (value) => {
	if (!value) return "—";

	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleString("ru-RU");
};

/**
 * Ключи Google по платформам.
 *
 * Ключей четыре, потому что ограничение у ключа Google только одно: либо
 * приложение Android, либо iOS, либо домен, либо IP. Один и тот же ключ во всех
 * четырёх строках — рабочий вариант, но тогда он ничем не ограничен, и любой,
 * кто вытащит его из приложения, сможет тратить наши деньги.
 *
 * Веб и сервер берут ключ у бэкенда на ходу, поэтому правка здесь применяется
 * сразу. Мобильные ключи нативный Google Maps SDK читает из самой сборки, и у
 * установленного приложения подменить их нечем — об этом страница говорит
 * прямо, иначе правка выглядела бы применённой.
 */
const GoogleApiKeys = () => {
	const [items, setItems] = useState([]);
	const [drafts, setDrafts] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const [savingPlatform, setSavingPlatform] = useState(null);
	const [status, setStatus] = useState(null);

	const reload = useCallback(async () => {
		setIsLoading(true);
		try {
			const loaded = await getGoogleApiKeys();
			setItems(loaded);
			setDrafts(
				loaded.reduce((acc, item) => ({ ...acc, [item.platform]: item.keyValue || "" }), {}),
			);
		} catch (error) {
			console.error("Load google api keys error:", error);
			setStatus({ ok: false, text: "Не удалось загрузить ключи" });
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		reload();
	}, [reload]);

	const onSave = async (platform) => {
		setSavingPlatform(platform);
		setStatus(null);
		try {
			const result = await saveGoogleApiKey({
				platform,
				keyValue: (drafts[platform] || "").trim(),
			});

			if (result?.isSuccess === false) {
				setStatus({ ok: false, text: result.message || "Не удалось сохранить" });
				return;
			}

			await reload();
			setStatus({
				ok: true,
				text: result?.message || "Сохранено",
			});
		} catch (error) {
			console.error("Save google api key error:", error);
			setStatus({ ok: false, text: "Не удалось сохранить" });
		} finally {
			setSavingPlatform(null);
		}
	};

	// Один ключ на всё — сейчас это рабочий вариант, и вбивать его четыре раза
	// руками незачем.
	const onCopyToAll = () => {
		const source = (drafts.web || "").trim();
		if (!source) {
			setStatus({ ok: false, text: "Сначала впишите ключ в строку «Веб»" });
			return;
		}

		setDrafts((previous) =>
			Object.keys(PLATFORMS).reduce((acc, platform) => ({ ...acc, [platform]: source }), previous),
		);
		setStatus({ ok: true, text: "Вписано во все строки. Сохраните каждую." });
	};

	return (
		<div className="gak">
			<div className="gak__head">
				<h2>Ключи Google</h2>
				<button type="button" className="gak__btn" onClick={onCopyToAll} disabled={isLoading}>
					Один ключ во все строки
				</button>
			</div>

			<p className="gak__hint">
				Карта и поиск мест. Ограничение у ключа Google только одно — либо
				приложение Android, либо iOS, либо домен, либо IP, — поэтому строк
				четыре. Один и тот же ключ везде работает, но такой ключ ничем не
				ограничен: кто вытащит его из приложения, сможет тратить наши деньги.
				В самом ключе должны быть включены Maps SDK for Android, Maps SDK for
				iOS, Maps JavaScript API и Places API.
			</p>

			{isLoading ? (
				<div className="gak__empty">Загрузка…</div>
			) : (
				<table className="gak__table">
					<thead>
						<tr>
							<th>Платформа</th>
							<th>Ключ</th>
							<th>Применяется</th>
							<th>Изменён</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{items.map((item) => {
							const meta = PLATFORMS[item.platform] ?? {};

							return (
								<tr key={item.platform} className={item.isSet ? "" : "isOff"}>
									<td>
										<div className="gak__platform">{platformLabel(item.platform)}</div>
										<div className="gak__purpose">{meta.purpose}</div>
									</td>
									<td>
										<input
											className="gak__input"
											value={drafts[item.platform] ?? ""}
											placeholder={meta.restriction}
											onChange={(e) => {
												setDrafts((p) => ({ ...p, [item.platform]: e.target.value }));
												setStatus(null);
											}}
										/>
									</td>
									<td className="gak__applies">
										{item.appliesWithoutRebuild ? (
											"сразу"
										) : (
											<span className="gak__rebuild">только с новой сборкой</span>
										)}
									</td>
									<td>{formatUpdatedAt(item.updatedAtUtc)}</td>
									<td className="gak__rowBtns">
										<button
											type="button"
											onClick={() => onSave(item.platform)}
											disabled={savingPlatform === item.platform}
										>
											{savingPlatform === item.platform ? "Сохранение…" : "Сохранить"}
										</button>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			)}

			<p className="gak__hint">
				Пустое поле стирает ключ: у веба пропадает карта, у сервера поиск мест
				уходит на запасной источник (OpenStreetMap). Ключи Android и iPhone
				нативный Google Maps SDK читает из самой сборки, поэтому у людей с
				установленным приложением они сменятся только после новой сборки в
				магазине — здесь они хранятся для учёта и для сборки.
			</p>

			{status && (
				<p className={`gak__status ${status.ok ? "isOk" : "isFail"}`}>{status.text}</p>
			)}
		</div>
	);
};

export default GoogleApiKeys;
